import React, { useEffect, useState } from 'react';

// Vue orthographique deck.gl (flipY : y vers le bas, comme les lignes de l'image) : passage image <-> écran, en px.
export function projection(view, width, height) {
  const [zx, zy] = Array.isArray(view.zoom) ? view.zoom : [view.zoom, view.zoom];
  const [sx, sy] = [2 ** zx, 2 ** zy];
  const [tx, ty] = view.target;
  return {
    scale: sx,
    toScreen: (x, y) => [width / 2 + (x - tx) * sx, height / 2 + (y - ty) * sy],
    toImage: (px, py) => [tx + (px - width / 2) / sx, ty + (py - height / 2) / sy],
  };
}

// Vue cadrée sur un rectangle de l'image (même marge que la vue par défaut de Viv, à zoomBackOff près).
export function fitView({ x0, y0, x1, y1 }, width, height, backOff = 0.25) {
  return {
    target: [(x0 + x1) / 2, (y0 + y1) / 2, 0],
    zoom: Math.log2(Math.min(width / (x1 - x0), height / (y1 - y0))) - backOff,
  };
}

const clamp = (v, lo, hi) => Math.round(Math.min(hi, Math.max(lo, v)));
const MIN_PX = 16;  // taille minimale du cadre, en px écran

// Poignées : bords (toute leur longueur) et coins ; chacune déplace un ou deux côtés du rectangle gardé.
const EDGES = [
  { id: 'top', keys: ['y0'] }, { id: 'bottom', keys: ['y1'] }, { id: 'left', keys: ['x0'] }, { id: 'right', keys: ['x1'] },
];
const CORNERS = [
  { id: 'top-left', keys: ['x0', 'y0'] }, { id: 'top-right', keys: ['x1', 'y0'] },
  { id: 'bottom-left', keys: ['x0', 'y1'] }, { id: 'bottom-right', keys: ['x1', 'y1'] },
];

// Réglage du recadrage : contour du rectangle gardé (coordonnées pleine résolution) et poignées à faire glisser
// depuis les bords de l'image. Suit la vue deck.gl (zoom, déplacement) : subscribe(fn) est appelé à chaque
// changement de vue.
export default function CropOverlay({ rect, width: W, height: H, containerRef, getView, subscribe, onChange }) {
  const [, redraw] = useState(0);
  useEffect(() => {
    const tick = () => redraw(n => n + 1);
    const unsubscribe = subscribe(tick);
    window.addEventListener('resize', tick);
    return () => { unsubscribe(); window.removeEventListener('resize', tick); };
  }, [subscribe]);

  const el = containerRef.current;
  const view = getView();
  if (!el || !view) return null;
  const { clientWidth: w, clientHeight: h } = el;
  const p = projection(view, w, h);
  const [left, top] = p.toScreen(rect.x0, rect.y0);
  const [right, bottom] = p.toScreen(rect.x1, rect.y1);
  // Bords limités à la partie visible (l'image peut déborder largement de l'écran une fois zoomée).
  const [vl, vr, vt, vb] = [Math.max(left, -20), Math.min(right, w + 20), Math.max(top, -20), Math.min(bottom, h + 20)];

  const startDrag = keys => e => {
    e.preventDefault();
    e.stopPropagation();
    const target = e.currentTarget;
    const box = el.getBoundingClientRect();
    const start = rect;
    target.setPointerCapture(e.pointerId);
    const move = ev => {
      const q = projection(getView(), box.width, box.height);
      const [x, y] = q.toImage(ev.clientX - box.left, ev.clientY - box.top);
      const min = MIN_PX / q.scale;
      const next = { ...start };
      if (keys.includes('x0')) next.x0 = clamp(x, 0, start.x1 - min);
      if (keys.includes('x1')) next.x1 = clamp(x, start.x0 + min, W);
      if (keys.includes('y0')) next.y0 = clamp(y, 0, start.y1 - min);
      if (keys.includes('y1')) next.y1 = clamp(y, start.y0 + min, H);
      onChange(next);
    };
    const up = () => {
      target.removeEventListener('pointermove', move);
      target.removeEventListener('pointerup', up);
      target.removeEventListener('pointercancel', up);
    };
    target.addEventListener('pointermove', move);
    target.addEventListener('pointerup', up);
    target.addEventListener('pointercancel', up);
  };

  const edgeStyle = {
    top: { left: vl, top, width: vr - vl },
    bottom: { left: vl, top: bottom, width: vr - vl },
    left: { left, top: vt, height: vb - vt },
    right: { left: right, top: vt, height: vb - vt },
  };
  const cornerStyle = {
    'top-left': { left, top }, 'top-right': { left: right, top },
    'bottom-left': { left, top: bottom }, 'bottom-right': { left: right, top: bottom },
  };
  return (
    <div className="crop-overlay">
      <div className="crop-frame" style={{ left, top, width: right - left, height: bottom - top }} />
      {EDGES.map(({ id, keys }) => (
        <div key={id} className={`crop-edge ${id}`} style={edgeStyle[id]} onPointerDown={startDrag(keys)}
          title="Drag to hide this edge"><span /></div>
      ))}
      {CORNERS.map(({ id, keys }) => (
        <div key={id} className={`crop-corner ${id}`} style={cornerStyle[id]} onPointerDown={startDrag(keys)} />
      ))}
    </div>
  );
}
